import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Divider,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FilterListIcon from "@mui/icons-material/FilterList";
import BadgeIcon from "@mui/icons-material/Badge";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import ApiCallService from "../../Service/ApiCallService";
import Spinner from "../common/Spinner";

const LEVEL_COLORS = {
  National: { bg: "#f9e0ff", border: "#5A2891", text: "#24034F" },
  State: { bg: "#f0fdf4", border: "#22c55e", text: "#15803d" },
  Regional: { bg: "#fffbeb", border: "#f59e0b", text: "#b45309" },
  District: { bg: "#fef2f2", border: "#ef4444", text: "#b91c1c" },
  Chapter: { bg: "#FAF5FF", border: "#9946DF", text: "#5A2891" },
};

const getStateLabel = (state) => {
  if (!state) return "";
  if (typeof state === "string") return state;
  return state.stateName || state.StateName || state.name || "";
};

const getRegionLabel = (region) => {
  if (!region) return "";
  if (typeof region === "string") return region;
  return region.regionName || region.name || "";
};

const getDistrictLabel = (district) => {
  if (!district) return "";
  if (typeof district === "string") return district;
  return district.districtName || district.name || "";
};

export default function CouncilHierarchyExplorer({ onSelectDesignation }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [states, setStates] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [hierarchyModalOpen, setHierarchyModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLevelFilter, setSelectedLevelFilter] = useState("All");

  // Cascading Selector State for Tab 1
  const [selectedCouncilId, setSelectedCouncilId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [selectedDistrictId, setSelectedDistrictId] = useState("");
  const [selectedCommitteeId, setSelectedCommitteeId] = useState("");
  const [filteredCommittees, setFilteredCommittees] = useState([]);
  const [filteredCommitteesLoading, setFilteredCommitteesLoading] = useState(false);

  // Add Designation Modal State
  const [addDesignationModalOpen, setAddDesignationModalOpen] = useState(false);
  const [targetCommitteeForAdd, setTargetCommitteeForAdd] = useState(null);
  const [newDesignationTitle, setNewDesignationTitle] = useState("");
  const [addDesignationSubmitting, setAddDesignationSubmitting] = useState(false);
  const [addDesignationError, setAddDesignationError] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleOpenAddDesignation = (committee) => {
    setTargetCommitteeForAdd(committee);
    setNewDesignationTitle("");
    setAddDesignationError("");
    setAddDesignationModalOpen(true);
  };

  const handleCloseAddDesignation = () => {
    if (addDesignationSubmitting) return;
    setAddDesignationModalOpen(false);
    setTargetCommitteeForAdd(null);
    setNewDesignationTitle("");
    setAddDesignationError("");
  };

  const handleSaveDesignation = async () => {
    if (!newDesignationTitle.trim()) {
      setAddDesignationError("Please enter a designation title.");
      return;
    }

    if (!targetCommitteeForAdd?._id) {
      setAddDesignationError("Invalid target committee.");
      return;
    }

    setAddDesignationSubmitting(true);
    setAddDesignationError("");

    try {
      const res = await ApiCallService.post(
        `/committeeDetails/committee/${targetCommitteeForAdd._id}/designation`,
        { title: newDesignationTitle.trim() }
      ).catch(async () => {
        const fallbackRes = await axios.post(
          `${process.env.REACT_APP_BASE_URL || ""}/committeeDetails/committee/${targetCommitteeForAdd._id}/designation`,
          { title: newDesignationTitle.trim() }
        );
        return fallbackRes.data;
      });

      if (res?.success && res.data) {
        const addedDesignation = res.data;

        // 1. Update filteredCommittees
        setFilteredCommittees((prev) =>
          prev.map((c) =>
            String(c._id) === String(targetCommitteeForAdd._id)
              ? {
                ...c,
                designations: [...(c.designations || []), addedDesignation],
              }
              : c
          )
        );

        // 2. Update hierarchyData
        setHierarchyData((prevHierarchy) =>
          prevHierarchy.map((council) => ({
            ...council,
            committees: (council.committees || []).map((comm) =>
              String(comm._id) === String(targetCommitteeForAdd._id)
                ? {
                  ...comm,
                  designations: [...(comm.designations || []), addedDesignation],
                }
                : comm
            ),
          }))
        );

        setSnackbarMessage(`Designation "${newDesignationTitle.trim()}" added successfully!`);
        setSnackbarOpen(true);
        handleCloseAddDesignation();
      } else {
        setAddDesignationError(res?.message || "Failed to add designation.");
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message || err.message || "Failed to add designation.";
      setAddDesignationError(errMsg);
    } finally {
      setAddDesignationSubmitting(false);
    }
  };

  const normalizeList = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.data?.data)) return res.data.data;
    return [];
  };

  // Initial Data Fetching
  const fetchHierarchy = async () => {
    setLoading(true);
    try {
      const res = await ApiCallService.get("/committeeDetails/hierarchy").catch(() =>
        axios.get(`${process.env.REACT_APP_BASE_URL || ""}/committeeDetails/hierarchy`).then((r) => r.data)
      );
      if (res?.success) {
        setHierarchyData(res.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch council hierarchy:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeographies = async () => {
    try {
      const [statesRes, regionsRes, districtsRes] = await Promise.all([
        ApiCallService.get("/states").catch(() =>
          axios.get(`${process.env.REACT_APP_BASE_URL || ""}/states`).then((r) => r.data).catch(() => [])
        ),
        ApiCallService.get("/regions").catch(() =>
          axios.get(`${process.env.REACT_APP_BASE_URL || ""}/regions`).then((r) => r.data).catch(() => [])
        ),
        ApiCallService.get("/districts").catch(() =>
          axios.get(`${process.env.REACT_APP_BASE_URL || ""}/districts`).then((r) => r.data).catch(() => [])
        ),
      ]);

      setStates(normalizeList(statesRes));
      setRegions(normalizeList(regionsRes));
      setDistricts(normalizeList(districtsRes));
    } catch (error) {
      console.error("Failed to load geographic masters:", error);
    }
  };

  useEffect(() => {
    fetchHierarchy();
    fetchGeographies();
  }, []);

  const selectedCouncil = useMemo(() => {
    return hierarchyData.find((c) => String(c._id) === String(selectedCouncilId));
  }, [hierarchyData, selectedCouncilId]);

  const availableStates = useMemo(() => {
    const stateMap = new Map();

    // 1. Extract any states from the selected council's committees
    if (selectedCouncil?.committees?.length) {
      selectedCouncil.committees.forEach((comm) => {
        if (comm.state && comm.state._id) {
          const sName = getStateLabel(comm.state);
          if (sName) {
            stateMap.set(String(comm.state._id), {
              _id: String(comm.state._id),
              stateName: sName,
            });
          }
        }
      });
    }

    // 2. Extract states from all hierarchy data
    hierarchyData.forEach((council) => {
      (council.committees || []).forEach((comm) => {
        if (comm.state && comm.state._id) {
          const sName = getStateLabel(comm.state);
          const sId = String(comm.state._id);
          if (sName && !stateMap.has(sId)) {
            stateMap.set(sId, { _id: sId, stateName: sName });
          }
        }
      });
    });

    // 3. Add all master states
    states.forEach((s) => {
      const sId = String(s._id);
      const sName = s.stateName || s.StateName || s.name || "";
      if (sName && !stateMap.has(sId)) {
        stateMap.set(sId, { _id: sId, stateName: sName });
      }
    });

    return Array.from(stateMap.values()).sort((a, b) =>
      a.stateName.localeCompare(b.stateName)
    );
  }, [states, selectedCouncil, hierarchyData]);

  const availableRegions = useMemo(() => {
    const regionMap = new Map();

    // From selected council committees
    if (selectedCouncil?.committees?.length) {
      selectedCouncil.committees.forEach((comm) => {
        if (
          comm.region &&
          comm.region._id &&
          (!selectedStateId || String(comm.state?._id || comm.state) === String(selectedStateId))
        ) {
          const rName = getRegionLabel(comm.region);
          if (rName) {
            regionMap.set(String(comm.region._id), {
              _id: String(comm.region._id),
              regionName: rName,
            });
          }
        }
      });
    }

    // From master regions matching selectedStateId
    regions.forEach((r) => {
      const rStateId = String(r.stateId?._id || r.stateId || r.state || "");
      if (!selectedStateId || rStateId === String(selectedStateId)) {
        const rId = String(r._id);
        const rName = r.regionName || r.name || "";
        if (rName && !regionMap.has(rId)) {
          regionMap.set(rId, { _id: rId, regionName: rName });
        }
      }
    });

    return Array.from(regionMap.values()).sort((a, b) =>
      a.regionName.localeCompare(b.regionName)
    );
  }, [regions, selectedStateId, selectedCouncil]);

  const availableDistricts = useMemo(() => {
    const districtMap = new Map();

    // From selected council committees
    if (selectedCouncil?.committees?.length) {
      selectedCouncil.committees.forEach((comm) => {
        if (
          comm.district &&
          comm.district._id &&
          (!selectedRegionId || String(comm.region?._id || comm.region) === String(selectedRegionId))
        ) {
          const dName = getDistrictLabel(comm.district);
          if (dName) {
            districtMap.set(String(comm.district._id), {
              _id: String(comm.district._id),
              districtName: dName,
            });
          }
        }
      });
    }

    // From master districts matching selectedRegionId
    districts.forEach((d) => {
      const dRegionId = String(d.regionId?._id || d.regionId || d.region || "");
      if (!selectedRegionId || dRegionId === String(selectedRegionId)) {
        const dId = String(d._id);
        const dName = d.districtName || d.name || "";
        if (dName && !districtMap.has(dId)) {
          districtMap.set(dId, { _id: dId, districtName: dName });
        }
      }
    });

    return Array.from(districtMap.values()).sort((a, b) =>
      a.districtName.localeCompare(b.districtName)
    );
  }, [districts, selectedRegionId, selectedCouncil]);

  useEffect(() => {
    if (!selectedCouncilId) {
      setFilteredCommittees([]);
      setSelectedCommitteeId("");
      return;
    }

    const loadScopedCommittees = async () => {
      setFilteredCommitteesLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_BASE_URL || "";
        const params = { councilId: selectedCouncilId };
        if (selectedStateId) params.stateId = selectedStateId;
        if (selectedRegionId) params.regionId = selectedRegionId;
        if (selectedDistrictId) params.districtId = selectedDistrictId;

        const res = await axios.get(`${baseUrl}/committeeDetails/committeesByCouncil`, { params });
        if (res.data?.success) {
          setFilteredCommittees(res.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching scoped committees:", err);
      } finally {
        setFilteredCommitteesLoading(false);
      }
    };

    loadScopedCommittees();
  }, [selectedCouncilId, selectedStateId, selectedRegionId, selectedDistrictId]);

  const activeCommittee = useMemo(() => {
    return filteredCommittees.find((c) => String(c._id) === String(selectedCommitteeId));
  }, [filteredCommittees, selectedCommitteeId]);

  const stats = useMemo(() => {
    let totalCommittees = 0;
    let totalDesignations = 0;
    let totalVariants = 0;

    hierarchyData.forEach((c) => {
      totalVariants += c.committeeVariants?.length || 0;
      totalCommittees += c.committees?.length || 0;
      c.committees?.forEach((comm) => {
        totalDesignations += comm.designations?.length || 0;
      });
    });

    return {
      councilsCount: hierarchyData.length,
      committeesCount: totalCommittees,
      designationsCount: totalDesignations,
      variantsCount: totalVariants,
    };
  }, [hierarchyData]);

  const filteredHierarchy = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return hierarchyData
      .filter((council) => {
        if (selectedLevelFilter !== "All" && council.level !== selectedLevelFilter) {
          return false;
        }
        return true;
      })
      .map((council) => {
        if (!query) return council;

        const matchesCouncil =
          council.councilName.toLowerCase().includes(query) ||
          council.level.toLowerCase().includes(query);

        const matchingCommittees = (council.committees || []).filter((comm) => {
          const nameMatch = comm.name?.toLowerCase().includes(query);
          const typeMatch = comm.committeeTypeName?.toLowerCase().includes(query);
          const stateMatch = getStateLabel(comm.state).toLowerCase().includes(query);
          const regionMatch = getRegionLabel(comm.region).toLowerCase().includes(query);
          const districtMatch = getDistrictLabel(comm.district).toLowerCase().includes(query);
          const subtypeMatch = comm.committeeSubtype?.toLowerCase().includes(query);
          const desigMatch = comm.designations?.some(
            (d) =>
              d.title?.toLowerCase().includes(query) ||
              d.responsibility?.toLowerCase().includes(query)
          );

          return (
            nameMatch || typeMatch || stateMatch || regionMatch || districtMatch || subtypeMatch || desigMatch
          );
        });

        if (matchesCouncil || matchingCommittees.length > 0) {
          return { ...council, committees: matchingCommittees.length > 0 ? matchingCommittees : council.committees };
        }
        return null;
      })
      .filter(Boolean);
  }, [hierarchyData, searchQuery, selectedLevelFilter]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Top Compact Dark Banner */}
      <Paper
        elevation={0}
        sx={{
          py: 1.5,
          px: { xs: 1.5, sm: 2.5 },
          mb: 2.5,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.secondary.LPMapSection} 0%, ${theme.palette.background.figma} 100%)`,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.primary.light}`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1, fontSize: "1.05rem" }}>
              <FilterListIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} /> Councils, Committees & Designations Finder
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mt: 0.2, fontSize: "0.78rem" }}>
              Cascading scope & designation finder for national, state, regional, and district councils.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AccountTreeIcon sx={{ fontSize: "16px !important" }} />}
              onClick={() => setHierarchyModalOpen(true)}
              sx={{
                backgroundColor: theme.palette.primary.main,
                "&:hover": { backgroundColor: theme.palette.primary.dark },
                borderRadius: 1.5,
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                py: 0.5,
                px: 1.5,
              }}
            >
              Councils, Committees & Designations Directory
            </Button>
            {/* <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: "15px !important" }} />}
              onClick={fetchHierarchy}
              sx={{
                color: "#e2e8f0",
                borderColor: "rgba(255, 255, 255, 0.2)",
                "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255, 255, 255, 0.08)" },
                borderRadius: 1.5,
                textTransform: "none",
                fontSize: "0.78rem",
                py: 0.5,
                px: 1.2,
              }}
            >
              Refresh
            </Button> */}
          </Box>
        </Box>

        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ py: 0.8, px: 1.5, borderRadius: 1.5, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.primary.light}` }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>COUNCILS</Typography>
              <Typography sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: "1.15rem", lineHeight: 1.2 }}>{stats.councilsCount}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ py: 0.8, px: 1.5, borderRadius: 1.5, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.primary.light}` }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>COMMITTEE TYPE</Typography>
              <Typography sx={{ fontWeight: 700, color: theme.palette.secondary.pillColor, fontSize: "1.15rem", lineHeight: 1.2 }}>{stats.variantsCount}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ py: 0.8, px: 1.5, borderRadius: 1.5, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.primary.light}` }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>COMMITTEES</Typography>
              <Typography sx={{ fontWeight: 700, color: "#22c55e", fontSize: "1.15rem", lineHeight: 1.2 }}>{stats.committeesCount}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ py: 0.8, px: 1.5, borderRadius: 1.5, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.primary.light}` }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.68rem", fontWeight: 600, letterSpacing: 0.5 }}>DESIGNATIONS</Typography>
              <Typography sx={{ fontWeight: 700, color: theme.palette.accent.main, fontSize: "1.15rem", lineHeight: 1.2 }}>{stats.designationsCount}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Feature: Interactive Scope & Designation Finder (No Tabs) */}
      <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.text.primary }}>
          Find Councils, Committees & Designations
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, fontSize: "0.85rem" }}>
          Select a Council to dynamically expose the relevant geographic scopes (State, Region, District) and filter committees & designations.
        </Typography>

        {/* Council Selector - Top Row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Council</InputLabel>
              <Select
                value={selectedCouncilId}
                label="Council"
                onChange={(e) => {
                  setSelectedCouncilId(e.target.value);
                  setSelectedStateId("");
                  setSelectedRegionId("");
                  setSelectedDistrictId("");
                  setSelectedCommitteeId("");
                }}
              >
                {hierarchyData.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.councilName} ({c.level})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Location Filters - Grouped Row */}
        {["State", "Regional", "District"].includes(selectedCouncil?.level) && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select
                  value={selectedStateId}
                  label="State"
                  onChange={(e) => {
                    setSelectedStateId(e.target.value);
                    setSelectedRegionId("");
                    setSelectedDistrictId("");
                    setSelectedCommitteeId("");
                  }}
                >
                  <MenuItem value="">
                    <em>-- All States --</em>
                  </MenuItem>
                  {availableStates.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.stateName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {["Regional", "District"].includes(selectedCouncil?.level) && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={!selectedStateId}>
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={selectedRegionId}
                    label="Region"
                    onChange={(e) => {
                      setSelectedRegionId(e.target.value);
                      setSelectedDistrictId("");
                      setSelectedCommitteeId("");
                    }}
                  >
                    <MenuItem value="">
                      <em>-- All Regions in State --</em>
                    </MenuItem>
                    {availableRegions.map((r) => (
                      <MenuItem key={r._id} value={r._id}>
                        {r.regionName || r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {selectedCouncil?.level === "District" && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={!selectedRegionId}>
                  <InputLabel>District</InputLabel>
                  <Select
                    value={selectedDistrictId}
                    label="District"
                    onChange={(e) => {
                      setSelectedDistrictId(e.target.value);
                      setSelectedCommitteeId("");
                    }}
                  >
                    <MenuItem value="">
                      <em>-- All Districts in Region --</em>
                    </MenuItem>
                    {availableDistricts.map((d) => (
                      <MenuItem key={d._id} value={d._id}>
                        {d.districtName || d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        )}

        {/* Committee Selector */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small" disabled={!selectedCouncilId || filteredCommitteesLoading}>
              <InputLabel>Select Committee</InputLabel>
              <Select
                value={selectedCommitteeId}
                label="Select Committee"
                onChange={(e) => setSelectedCommitteeId(e.target.value)}
              >
                {filteredCommittees.map((comm) => {
                  const stateLbl = getStateLabel(comm.state);
                  return (
                    <MenuItem key={comm._id} value={comm._id}>
                      {comm.name} {comm.committeeSubtype ? `[${comm.committeeSubtype.toUpperCase()}]` : ""}{" "}
                      {stateLbl ? `(${stateLbl})` : ""}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {activeCommittee && (
          <Card sx={{ mt: 3, borderRadius: 2, border: "1px solid #e0e0e0" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {activeCommittee.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Council: <strong>{selectedCouncil?.councilName} ({selectedCouncil?.level})</strong> &bull; Committee Type: <strong>{activeCommittee.committeeTypeName || "Standard"}</strong>
                  </Typography>
                </Box>
                {activeCommittee.committeeSubtype && (
                  <Chip label={`Subtype: ${activeCommittee.committeeSubtype.toUpperCase()}`} color="primary" />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                  <AssignmentIndIcon color="primary" /> Designations Available ({activeCommittee.designations?.length || 0}):
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAddDesignation(activeCommittee)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: theme.palette.primary.main,
                    "&:hover": { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  Add Designation
                </Button>
              </Box>

              {activeCommittee.designations?.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 3, backgroundColor: theme.palette.background.slate, borderRadius: 2, border: `1px dashed ${theme.palette.primary.light}` }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    No designations found under this committee.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAddDesignation(activeCommittee)}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Add First Designation
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {activeCommittee.designations.map((desig, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={desig._id || idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: theme.palette.background.slate,
                          border: `1px solid ${theme.palette.primary.light}`,
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          transition: "all 0.2s",
                          "&:hover": {
                            borderColor: theme.palette.secondary.figma,
                            backgroundColor: theme.palette.primary.light,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                          <BadgeIcon sx={{ color: theme.palette.primary.main, fontSize: 22, flexShrink: 0 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary, wordBreak: "break-word" }}>
                            {desig.title}
                          </Typography>
                        </Box>
                        {/* Responsibility commented out as requested */}
                        {/* <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem" }}>
                          {desig.responsibility || "Responsibility details not specified."}
                        </Typography> */}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        )}
      </Paper>

      {/* Hierarchy Tree & Directory Modal Dialog */}
      <Dialog
        open={hierarchyModalOpen}
        onClose={() => setHierarchyModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2.5, maxHeight: "90vh" },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1.5, borderBottom: "1px solid #e2e8f0" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountTreeIcon sx={{ color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                Councils & Committees Hierarchy Directory
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Explore full tree structure across national, state, regional, and district councils.
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setHierarchyModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2} sx={{ mb: 2.5 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search councils, committees, designations, states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Council Level Filter</InputLabel>
                <Select
                  value={selectedLevelFilter}
                  label="Council Level Filter"
                  onChange={(e) => setSelectedLevelFilter(e.target.value)}
                >
                  <MenuItem value="All">All Levels (National, State, Regional, District)</MenuItem>
                  <MenuItem value="National">National (NEC)</MenuItem>
                  <MenuItem value="State">State (SEC)</MenuItem>
                  <MenuItem value="Regional">Regional (REC)</MenuItem>
                  <MenuItem value="District">District (DEC)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <Spinner />
            </Box>
          ) : filteredHierarchy.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No councils or committees found matching your filter criteria.
            </Alert>
          ) : (
            filteredHierarchy.map((council) => {
              const colorConfig = LEVEL_COLORS[council.level] || LEVEL_COLORS.National;

              return (
                <Accordion
                  key={council._id}
                  defaultExpanded={true}
                  sx={{
                    mb: 2,
                    border: `1px solid ${colorConfig.border}`,
                    borderRadius: "12px !important",
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: colorConfig.bg,
                      borderBottom: `1px solid ${colorConfig.border}33`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", pr: 2 }}>
                      <BusinessIcon sx={{ color: colorConfig.text }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colorConfig.text }}>
                          {council.councilName} ({council.level} Council)
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          Code: {council.code || "N/A"} &bull; {council.committees?.length || 0} Committees
                        </Typography>
                      </Box>
                      <Chip
                        label={council.level}
                        size="small"
                        sx={{
                          backgroundColor: colorConfig.border,
                          color: "#fff",
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 2.5, backgroundColor: "#fafbfc" }}>
                    {council.committeeVariants?.length > 0 && (
                      <Box sx={{ mb: 2.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1 }}>
                          Committee Variants ({council.committeeVariants.length}):
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {council.committeeVariants.map((v) => (
                            <Chip
                              key={v._id}
                              label={`${v.name}${v.code ? ` (${v.code})` : ""}`}
                              size="small"
                              variant="outlined"
                              sx={{ borderColor: "#cbd5e1", backgroundColor: "#fff", fontWeight: 500 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1.5 }}>
                      Committees & Associated Roles:
                    </Typography>

                    {council.committees?.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No committees configured under this council.
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {council.committees.map((comm) => (
                          <Grid item xs={12} md={6} key={comm._id}>
                            <Card
                              variant="outlined"
                              sx={{
                                height: "100%",
                                borderRadius: 2,
                                borderColor: "#e0e0e0",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  borderColor: colorConfig.border,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                                },
                              }}
                            >
                              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                                      {comm.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                                      Variant: {comm.committeeTypeName || "Standard"}
                                    </Typography>
                                  </Box>
                                  {comm.committeeSubtype && (
                                    <Chip
                                      label={comm.committeeSubtype.toUpperCase()}
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                                    />
                                  )}
                                </Box>

                                {(comm.state || comm.region || comm.district) && (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                                    {getStateLabel(comm.state) && (
                                      <Chip
                                        icon={<LocationOnIcon sx={{ fontSize: "14px !important" }} />}
                                        label={`State: ${getStateLabel(comm.state)}`}
                                        size="small"
                                        sx={{ height: 22, fontSize: "0.72rem", backgroundColor: "#f1f5f9" }}
                                      />
                                    )}
                                    {comm.region && (
                                      <Chip
                                        label={`Region: ${comm.region.name}`}
                                        size="small"
                                        sx={{ height: 22, fontSize: "0.72rem", backgroundColor: "#f1f5f9" }}
                                      />
                                    )}
                                    {comm.district && (
                                      <Chip
                                        label={`District: ${comm.district.name}`}
                                        size="small"
                                        sx={{ height: 22, fontSize: "0.72rem", backgroundColor: "#f1f5f9" }}
                                      />
                                    )}
                                  </Box>
                                )}

                                <Divider sx={{ my: 1 }} />

                                <Box>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#475569",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <BadgeIcon sx={{ fontSize: 16 }} /> Designations ({comm.designations?.length || 0}):
                                    </Typography>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                                      onClick={() => handleOpenAddDesignation(comm)}
                                      sx={{ fontSize: "0.7rem", py: 0.2, px: 0.8, textTransform: "none", minWidth: 0 }}
                                    >
                                      Add
                                    </Button>
                                  </Box>

                                  {comm.designations?.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                      No designations specified.
                                    </Typography>
                                  ) : (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                                      {comm.designations.map((d) => (
                                        <Chip
                                          key={d._id}
                                          label={d.title}
                                          size="small"
                                          onClick={() => onSelectDesignation && onSelectDesignation({ council, committee: comm, designation: d })}
                                          sx={{
                                            backgroundColor: theme.palette.background.slate,
                                            border: `1px solid ${theme.palette.primary.light}`,
                                            fontWeight: 500,
                                            fontSize: "0.75rem",
                                            cursor: onSelectDesignation ? "pointer" : "default",
                                            "&:hover": {
                                              backgroundColor: colorConfig.bg,
                                              borderColor: colorConfig.border,
                                            },
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </DialogContent>
        {/* <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={() => setHierarchyModalOpen(false)} variant="contained" sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions> */}
      </Dialog>

      {/* Add Designation Modal Dialog */}
      <Dialog
        open={addDesignationModalOpen}
        onClose={handleCloseAddDesignation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BadgeIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Add New Designation
            </Typography>
          </Box>
          <IconButton onClick={handleCloseAddDesignation} size="small" disabled={addDesignationSubmitting}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {targetCommitteeForAdd && (
            <Alert severity="info" sx={{ mb: 2.5, py: 0.5, fontSize: "0.85rem" }}>
              Committee: <strong>{targetCommitteeForAdd.name}</strong>
              {targetCommitteeForAdd.state && (
                <span> &bull; State: <strong>{getStateLabel(targetCommitteeForAdd.state)}</strong></span>
              )}
            </Alert>
          )}

          {addDesignationError && (
            <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
              {addDesignationError}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Designation Title"
            placeholder="e.g. State Vice President - Public Relations"
            value={newDesignationTitle}
            onChange={(e) => {
              setNewDesignationTitle(e.target.value);
              if (addDesignationError) setAddDesignationError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSaveDesignation();
              }
            }}
            disabled={addDesignationSubmitting}
            helperText="Enter the official role or designation title for this committee."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseAddDesignation}
            disabled={addDesignationSubmitting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveDesignation}
            variant="contained"
            disabled={addDesignationSubmitting || !newDesignationTitle.trim()}
            startIcon={addDesignationSubmitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{
              backgroundColor: theme.palette.primary.main,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
              fontWeight: 600,
            }}
          >
            {addDesignationSubmitting ? "Adding..." : "Save Designation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
